import React, { useState } from "react";
import styled from "styled-components";
import {
  formatDate,
  getStatusColor,
  getStatusIcon,
  TimelineWrapper,
  Timeline,
  TimelineDot,
  TimelineEvent,
  EventContent,
  EventHeader,
  EventLocation,
  EventTime,
  EventType,
  AWBNumber,
  LastUpdated,
  StatusBadge,
  StatusIcon,
  ExpandIcon,
  ShipmentCard,
  ShipmentHeader,
} from "../index";

const TrackingTimeLine = ({ current, setCurrent, trackingData }) => {
  const [expandedShipment, setExpandedShipment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 5;

  // Pagination logic
  const filteredShipments = trackingData?.length
    ? trackingData.filter((shipment) =>
        shipment.awb_no.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const totalPages = Math.ceil(filteredShipments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentShipments = filteredShipments.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedShipment(null);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <Container>
      <HeaderSection>
        <SearchContainer>
          <SearchIcon>{"🔍"}</SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search by AWB number..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
              setExpandedShipment(null);
            }}
          />
          {searchTerm && (
            <ClearButton type="button" onClick={() => setSearchTerm("")}>
              ✕
            </ClearButton>
          )}
        </SearchContainer>
      </HeaderSection>

      <ContentArea>
        <ShipmentsContainer>
          {currentShipments.map((shipment) => (
            <ShipmentCard
              key={shipment.awb_no}
              isExpanded={expandedShipment === shipment.awb_no}
              onClick={() =>
                setExpandedShipment(
                  expandedShipment === shipment.awb_no ? null : shipment.awb_no
                )
              }
            >
              <ShipmentHeader>
                <ShipmentInfo>
                  <AWBNumber>{shipment.awb_no}</AWBNumber>
                  <LastUpdated>{formatDate(shipment.status_date)}</LastUpdated>
                </ShipmentInfo>
                <StatusBadge colors={getStatusColor(shipment.status)}>
                  <StatusIcon>{getStatusIcon(shipment.status)}</StatusIcon>
                  {shipment.status}
                </StatusBadge>
                <ExpandIcon isExpanded={expandedShipment === shipment.awb_no}>
                  ▼
                </ExpandIcon>
              </ShipmentHeader>

              {expandedShipment === shipment.awb_no && (
                <TimelineWrapper>
                  <Timeline>
                    {shipment.event.map((scan, idx) => (
                      <TimelineEvent
                        key={idx}
                        isLast={idx === shipment.event.length - 1}
                      >
                        <TimelineDot />
                        <EventContent>
                          <EventHeader>
                            <EventLocation>
                              {typeof scan.location === "string"
                                ? scan.location
                                : ""}
                            </EventLocation>
                            <EventTime>
                              {formatDate(scan.status_date)}
                            </EventTime>
                          </EventHeader>
                          <EventType>{scan.remarks}</EventType>
                        </EventContent>
                      </TimelineEvent>
                    ))}
                  </Timeline>
                </TimelineWrapper>
              )}
            </ShipmentCard>
          ))}
        </ShipmentsContainer>
      </ContentArea>

      <PaginationSection>
        <PaginationInfo>
          Showing {currentShipments.length === 0 ? 0 : startIndex + 1} to{" "}
          {Math.min(endIndex, filteredShipments.length)} of{" "}
          {filteredShipments.length}
        </PaginationInfo>

        <PaginationContainer>
          <PaginationButton
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </PaginationButton>

          {generatePageNumbers().map((page, idx) => (
            <PageButton
              key={idx}
              type="button"
              onClick={() => typeof page === "number" && handlePageChange(page)}
              active={page === currentPage}
              disabled={page === "..."}
            >
              {page}
            </PageButton>
          ))}

          <PaginationButton
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </PaginationButton>
        </PaginationContainer>
      </PaginationSection>
    </Container>
  );
};

export default TrackingTimeLine;

const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 35px;
`;

export const HeaderSection = styled.div`
  background: linear-gradient(135deg, #00838c 0%);
  padding: 24px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
`;

const ContentArea = styled.div`
  padding: 16px;
  min-height: 400px;
`;

const ShipmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ShipmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PaginationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e8e8e8;
  flex-wrap: wrap;
  gap: 16px;
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const PaginationInfo = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const PaginationContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

const PaginationButton = styled.button.attrs({ type: "button" })`
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  color: #00838c;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #00838c;
    color: white;
    border-color: #00838c;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageButton = styled.button.attrs({ type: "button" })`
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  background: ${(props) => (props.active ? "#00838c" : "white")};
  color: ${(props) => (props.active ? "white" : "#00838c")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #00838c;
    color: white;
    border-color: #00838c;
  }
  &:disabled {
    cursor: default;
    color: #999;
    border-color: #ddd;
  }
`;

export const SearchContainer = styled.div`
  position: relative;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 10px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  pointer-events: none;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  transition: all 0.3s;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 0 12px rgba(255, 255, 255, 0.1);
  }
`;

const ClearButton = styled.button.attrs({ type: "button" })`
  position: absolute;
  right: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 16px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;
